import { Seasons } from 'common';
import Data from 'data';
import { clone } from 'utils';
import { computeStats, getStats } from './stats';

export function itemGetDefaultType(profile, slot) {
  let types;
  if (slot === "offhand") {
    types = Data.getOffhandTypes(profile);
  } else {
    types = Data.itemSlots[slot].types;
  }
  types = Object.keys(types).filter(type => {
    if (type === "custom" || type === "customwpn") return false;
    const typeInfo = Data.itemTypes[type];
    return !typeInfo.classes || typeInfo.classes.includes(profile.class);
  });
  return types.length === 1 ? types[0] : null;
}

export function itemGetLimits(item, src, dst) {
  const affixesVar = Data.getItemAffixesById(item.id, item.ancient, false);
  let affixesReq = Data.getItemAffixesById(item.id, item.ancient, "only");

  const getLimit = (stat, merged) => {
    let args = 0;
    if (stat === "custom" && affixesReq.custom) {
      if (affixesReq.custom.options) {
        affixesReq = Data.getItemAffixesById(item.customId || affixesReq.custom.options[0], item.ancient, "only");
        if (!affixesReq?.custom) return null;
      }
      args = (affixesReq.custom.args == null ? 1 : affixesReq.custom.args);
    } else if (Data.stats[stat]) {
      args = Data.stats[stat].args;
    } else {
      return null;
    }

    if (affixesReq[stat]) {
      if (affixesVar[stat] && merged) {
        const result = {...affixesReq[stat], merged: true, args};
        const src = affixesVar[stat];
        if (Data.stats[stat] && Data.stats[stat].dr) {
          result.min = 100 - 0.01 * (100 - (result.min || 0)) * (100 - (src.min || 0));
          result.max = 100 - 0.01 * (100 - (result.max || 0)) * (100 - (src.max || 0));
          result.step = "any";
        } else {
          result.min = (result.min || 0) + (src.min || 0);
          result.max = (result.max || 0) + (src.max || 0);
        }
        return result;
      } else {
        return {...affixesReq[stat], required: true, args};
      }
    } else if (affixesVar[stat]) {
      return {...affixesVar[stat], args};
    } else {
      return null;
    }
  };

  const limits = {};
  for (const stat of (src || Object.keys(item.stats))) {
    const limit = stat && getLimit(stat, item.merged && item.merged[stat]);
    if (limit) limits[stat] = limit;
  }
  if (dst) for (const stat of dst) {
    const limit = stat && getLimit(stat, !!affixesReq[stat]);
    if (limit) limits[stat] = limit;
  }
  return limits;
}

export function itemGetCounts(item) {
  const counts = Data.getStatCount(item.id);
  if (item.id === "Unique_Ring_021_x1") {
    // some weird Manald Heal (Legacy) logic
    if (item.stats.spiritregen || item.stats.wrathregen) {
      counts[0] += 1;
      counts[1] -= 1;
    }
  }

  let usedPrimary = 0;
  let usedSecondary = 0;
  let gift = 0, caldesanns = false, sanctified = false;
  for (const stat of Object.keys(item.stats)) {
    if (!Data.stats[stat]) continue;
    const data = Data.stats[stat];
    if (data.base) continue;
    let weight = 1;
    if (item.merged && item.merged[stat]) weight = 2;
    if (data.caldesanns) {
      weight = 0;
      caldesanns = true;
    }
    if (data.sanctified) {
      weight = 0;
      sanctified = true;
    }
    if (data.secondary) {
      usedSecondary += weight;
    } else {
      usedPrimary += weight;
    }
    if (stat === "sockets") {
      gift += 1;
    }
  }

  let slotType = Data.itemById[item.id].type;
  slotType = Data.itemTypes[slotType] && Data.itemTypes[slotType].slot;
  if (slotType === "onehand" || slotType === "twohand") {
    usedPrimary -= gift;
  }

  return {
    maxPrimary: counts[0],
    maxSecondary: counts[1],
    usedPrimary,
    usedSecondary,
    caldesanns,
    sanctified,
  };
}

export function itemSlotValid(charClass, slot) {
  const slotInfo = Data.itemSlots[slot];
  return slotInfo && (!slotInfo.classes || slotInfo.classes.includes(charClass));
}

export function itemAllowed(profile, slot, id, follower) {
  if (!id || !Data.itemById[id]) return false;
  const charClass = follower ? profile.follower : profile.class;
  if (!itemSlotValid(charClass, slot)) return false;

  const item = Data.itemById[id];
  if (item.classes && !item.classes.includes(charClass)) return false;

  if (item.quality === "ethereal" && profile.seasonal !== Seasons.Ethereal) return false;

  const types = (slot === "offhand" ? Data.getOffhandTypes(profile) : Data.itemSlots[slot].types);
  const itemType = types[item.type];
  if (!itemType || (itemType.classes && !itemType.classes.includes(charClass))) return false;

  return true;
}

export function itemPerfection(state, data, slot) {
  let statsBase;
  const profile = state.profiles[state.activeProfile];
  if (!data) {
    data = profile.items[slot];
    if (!data || !Data.itemById[data.id]) return;
    statsBase = getStats(state);
  } else if (Data.itemById[data.id]) {
    if (!slot) slot = Object.keys(profile.items).find(slot => profile.items[slot] && profile.items[slot].id === data.id);
    if (!slot) slot = Object.keys(Data.itemSlots).find(slot => itemAllowed(profile, slot, data.id));
    if (!slot) return;
    statsBase = computeStats(state, replSlot => {
      if (slot === replSlot) return data;
    });
  } else {
    return;
  }

  const limits = itemGetLimits(data);
  const dataMin = {...data, stats: clone(data.stats, true)};
  const dataMax = {...data, stats: clone(data.stats, true)};

  const info = {stats: {}, total: 0};
  let count = 0;

  for (const stat in data.stats) {
    const range = limits[stat];
    if (!range || range.args <= 0 || range.max == null || range.min === range.max) continue;
    let ratio = (data.stats[stat][0] - range.min) / (range.max - range.min);
    if (range.best === "min") {
      ratio = 1.0 - ratio;
      dataMin.stats[stat][0] = range.max;
      dataMax.stats[stat][0] = range.min;
    } else {
      dataMin.stats[stat][0] = range.min;
      dataMax.stats[stat][0] = range.max;
    }
    if (range.max2 != null && range.min2 !== range.max2) {
      ratio = 0.5 * (ratio + ((data.stats[stat][1] || 0) - range.min2) / (range.max2 - range.min2));
      dataMin.stats[stat][1] = range.min2;
      dataMax.stats[stat][1] = range.max2;
    }

    info.stats[stat] = ratio;
    if (stat === "custom" || (!Data.stats[stat].base && !Data.stats[stat].secondary)) {
      info.total += ratio;
      count += 1;
    }
  }
  info.total = (count ? info.total / count : 1);

  const statsMin = computeStats(state, replSlot => {
    if (replSlot === slot) return dataMin;
  });
  const statsMax = computeStats(state, replSlot => {
    if (replSlot === slot) return dataMax;
  });

  info.dps = (statsMax.info.dps > statsMin.info.dps + 1 ?
    (statsBase.info.dps - statsMin.info.dps) / (statsMax.info.dps - statsMin.info.dps) : 1);
  info.dpsDelta = Math.max(0, statsMax.info.dps - statsBase.info.dps);
  info.toughness = (statsMax.info.toughness > statsMin.info.toughness + 1 ?
    (statsBase.info.toughness - statsMin.info.toughness) / (statsMax.info.toughness - statsMin.info.toughness) : 1);
  info.toughnessDelta = Math.max(0, statsMax.info.toughness - statsBase.info.toughness);
  return info;
}

export function sameLimits(src, dst) {
  if (!src || !dst) return false;
  if (src.args !== dst.args) return false;
  if (src.args >= 1 && (src.min !== dst.min || src.max !== dst.max || (src.step || 1) !== (dst.step || 1))) return false;
  if (src.args >= 2 && (src.min2 !== dst.min2 || src.max2 !== dst.max2 || (src.step2 || 1) !== (dst.step2 || 1))) return false;
  return true;
}

function valueMix(best, min, max, step, percent) {
  if (best === "min") {
    const tmp = min;
    min = max;
    max = tmp;
  }
  if (percent == null) {
    return max;
  } else {
    let value = min + (max - min) * (percent / 100);
    if (!isNaN(step)) {
      value = Math.round(value / step) * step;
      value = Math.max(min, Math.min(max, value));
    }
    return value;
  }
}

export function defaultValue(limits, prev, charClass, percent) {
  const { min=0, max=Infinity, min2=0, max2=Infinity, step=1, step2=1, args=1, best } = limits;

  if (args === 0) {
    return [];
  } else if (args < 0) {
    if (charClass) {
      return [Object.keys(Data.passives[charClass]).filter(id => !limits.exclude?.includes(id))[0]];
    } else {
      return [Object.keys(Data.allPassives).filter(id => !limits.exclude?.includes(id))[0]];
    }
  }

  const value = [];

  if (args >= 1) {
    if (prev && prev[0] != null) {
      value.push(Math.min(max, Math.max(min, !step || isNaN(step) ? prev[0] : Math.round(prev[0] / step) * step)));
    } else {
      value.push(valueMix(best, min, max, step, percent));
    }
  }
  if (args >= 2) {
    if (prev && prev[1] != null) {
      value.push(Math.min(max2, Math.max(min2, !step2 || isNaN(step2) ? prev[1] : Math.round(prev[1] / step2) * step2)));
    } else {
      value.push(valueMix(best, min2, max2, step2, percent));
    }
  }
  return value;
}

export function itemAncient(item) {
  const base = Data.itemById[item.id];
  if (!base) return false;
  if (base.quality === "legendary" || base.quality === "set") {
    return item.ancient;
  }
  if (base.quality === "ethereal") {
    return true;
  }
  return false;
}
